/**
 * Platform-agnostic Supabase binding.
 *
 * Packages (core, brain) import `supabase` from here. The concrete client is
 * created and injected by each app at boot:
 *
 *   // apps/web/src/main.tsx
 *   import { createClient } from '@supabase/supabase-js';
 *   import { setSupabaseClient } from '@4cm/core/supabase';
 *   setSupabaseClient(createClient(import.meta.env.VITE_SUPABASE_URL, import.meta.env.VITE_SUPABASE_ANON_KEY));
 *
 *   // apps/mobile — future — injects its own client built with expo-constants
 *   // env vars.
 *
 * The exported `supabase` is a Proxy that transparently delegates to the
 * injected client. No call-site changes across the codebase.
 */
import type { SupabaseClient } from '@supabase/supabase-js';

let _client: SupabaseClient | null = null;

export function setSupabaseClient(client: SupabaseClient): void {
  _client = client;
}

export function getSupabaseClient(): SupabaseClient {
  if (!_client) {
    throw new Error(
      '[@4cm/core] Supabase client not initialised — call setSupabaseClient() at app boot.',
    );
  }
  return _client;
}

/** Transparent delegating binding — every call resolves against the injected
 *  client at call time, so rules like `import { supabase } from '...'` keep
 *  working unchanged in moved code. */
export const supabase = new Proxy({} as SupabaseClient, {
  get(_, prop) {
    return (getSupabaseClient() as any)[prop];
  },
}) as SupabaseClient;
